import React, { useState, useEffect } from 'react'

import Cookies from 'js-cookie'
import styled from 'styled-components'

import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors, BrandTokens } from 'src/utilities/Colors'

import { Paragraph, StyledLink } from './Typography'
import { BrandButton } from '../partials/Button'
import { Container } from '../partials/Container'

const BannerBarWrapper = styled.div<{
  backgroundColor: string
  secondary: boolean
}>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${BrandColors.white};
  padding: 0.5rem 0;
  /* if secondary is true set different padding */
  ${({ secondary }) =>
    secondary &&
    `
    padding: 1.5rem 0;
  `}
  text-align: center;
  display: none;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 10000;

  @media (min-width: 768px) {
    display: block;
  }
`

const BannerBarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
  text-align: center;

  /* make button smaller */
  a div {
    padding-block: 0.5rem;
    line-height: 1;
    height: auto;
  }

  @media (min-width: ${Breakpoints.sm}px) {
    flex-direction: row;
  }
`

const BannerBarContentCopy = styled.div`
  flex-shrink: 0;
  > * {
    display: inline;
  }
  strong {
    text-transform: uppercase;
  }
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${BrandColors.white};
  cursor: pointer;
  font-size: 2.5rem;
  line-height: 1;
  position: absolute;
  right: 1.5rem;
  top: 0.25rem;
  z-index: 1;
`

export const BannerBar = ({
  backgroundColor = BrandTokens.backgroundDark,
  btnText,
  btnUrl,
  copy,
  secondary = false,
  showCloseButton = true,
  title
}: {
  backgroundColor?: string
  btnText: string
  btnUrl: string
  copy: string
  secondary?: boolean
  showCloseButton?: boolean
  title: string
}) => {
  // Fix for Cumulative Layout Shift (CLS)
  let defaultState: boolean | null
  if (showCloseButton) {
    defaultState = null
  } else {
    defaultState = true
  }
  const [isVisible, setIsVisible] = useState<boolean | null>(defaultState)

  useEffect(() => {
    const storedDate = Cookies.get('bannerHideDate')
    const currentDate = new Date()

    if (storedDate) {
      const bannerHideDate = new Date(storedDate)
      if (currentDate < bannerHideDate) {
        setIsVisible(false)
      } else {
        setIsVisible(true)
      }
    } else {
      setIsVisible(true)
    }
  }, [])

  const handleBannerClose = () => {
    const consentIsGiven = window._iub.cs.consent.consent
    const currentDate = new Date()
    const bannerHideDate = new Date(currentDate)
    bannerHideDate.setDate(currentDate.getDate() + 1)

    // make sure it's ok to set cookies
    if (consentIsGiven) {
      Cookies.set('bannerHideDate', bannerHideDate.toString(), { expires: 1 })
    }
    setIsVisible(false)
  }

  /*
    When the component first mounts, isVisible is set to null.
    This represents a "loading" state where the component is
    reading from the cookie (or local storage). We don't want
    to display anything yet, because we don't know if the banner
    should be visible or not.
  */
  if (isVisible === null) {
    return null
  }

  /*
    After reading from the cookie, isVisible will be set to either
    true or false. If isVisible is false, it means the user has
    clicked the close button within the last day, so we don't want
    to display the banner.
  */
  if (!isVisible) {
    return null
  }

  return (
    <BannerBarWrapper backgroundColor={backgroundColor} secondary={secondary}>
      <Container>
        <BannerBarContent>
          <BannerBarContentCopy>
            <p className='paragraph text-sm'>
              <strong className='pr-2'>{title}</strong> {copy}
            </p>
          </BannerBarContentCopy>

          <StyledLink
            to={btnUrl}
            color={BrandColors.white}
            hoverColor={BrandColors.electric100}
            fontWeight='400'
            fontSize='1'
            arrow
          >
            {btnText}
          </StyledLink>

          {/* {showCloseButton && (
            <CloseButton aria-label='Close' onClick={handleBannerClose}>
              &times;
            </CloseButton>
          )} */}
        </BannerBarContent>
      </Container>
    </BannerBarWrapper>
  )
}
